import type { MessageKey } from "@erp/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Panel } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { useFormat, useT } from "~/lib/i18n";
import { HelpTip } from "~/components/ui/help-tip";

export const Route = createFileRoute("/_app/settings/health")({ component: HealthPage });

const checkLabel = (check: string) => `integrity.checks.${check}` as MessageKey;

function HealthPage() {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["integrity"], queryFn: () => unwrap(api.integrity.get()) });
  const run = useMutation({
    mutationFn: () => unwrap(api.integrity.run.post()),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["integrity"] }),
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });
  if (query.isPending) return <Skeleton className="h-[320px] w-full" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const { worker, lastRun, checkCount, errors, restoreCheck } = query.data;

  return (
    <div className="flex flex-col gap-4">
      <Panel className="flex flex-col gap-4 p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-medium">{t("integrity.title")}</h2>
            <p className="text-sm text-fg-3">
              {lastRun ? t("integrity.lastRun", { date: format.date(lastRun.at, "datetime") }) : t("integrity.never")}
            </p>
          </div>
          <span className="inline-flex items-center gap-0.5">
            <Button variant="secondary" onClick={() => run.mutate()} disabled={run.isPending}>
              <RefreshCw className={run.isPending ? "animate-spin" : undefined} />
              {run.isPending ? t("integrity.running") : t("integrity.run")}
            </Button>
            <HelpTip id="settings.health" />
          </span>
        </div>
        {lastRun && lastRun.issues.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-success">
            <CheckCircle2 className="size-4 shrink-0" />
            {t("integrity.allGood", { count: checkCount })}
          </p>
        ) : null}
        {lastRun && lastRun.issues.length > 0 ? (
          <div className="flex flex-col gap-3">
            <p className="flex items-center gap-2 text-sm text-danger">
              <AlertTriangle className="size-4 shrink-0" />
              {t("integrity.issues", { count: lastRun.issues.length })}
            </p>
            <ul className="flex flex-col gap-3">
              {lastRun.issues.map((issue) => (
                <li key={issue.check} className="rounded-control border border-line p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">{t(checkLabel(issue.check))}</span>
                    <Badge tone="danger">{t("integrity.occurrences", { count: issue.count })}</Badge>
                  </div>
                  <ul className="mt-2 flex flex-col gap-1 text-xs break-all text-fg-2 tabular">
                    {issue.samples.map((sample) => (
                      <li key={sample}>{sample}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Panel>
      <Panel className="flex flex-col gap-3 p-4 md:p-5">
        <h2 className="font-medium">{t("integrity.errors.title")}</h2>
        {errors.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-success">
            <CheckCircle2 className="size-4 shrink-0" />
            {t("integrity.errors.empty")}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {errors.map((e) => (
              <li key={e.id} className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={e.source === "web" ? "info" : "danger"}>{t(`integrity.errors.sources.${e.source}`)}</Badge>
                  <span className="min-w-0 text-sm break-words">{e.message}</span>
                </div>
                <p className="text-xs text-fg-3">
                  {String(e.detail.where ?? "")} ·{" "}
                  {t("integrity.errors.seen", { count: e.count, date: format.date(e.lastSeenAt, "datetime") })}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel className="flex flex-col gap-1 p-4 md:p-5">
        <span className="flex items-center gap-0.5">
          <h2 className="font-medium">{t("integrity.worker")}</h2>
          <HelpTip id="auto.jobs" />
        </span>
        <p className={worker.stale ? "text-sm text-danger" : "text-sm text-fg-2"}>
          {!worker.lastBeatAt
            ? t("integrity.workerNever")
            : worker.stale
              ? t("integrity.workerStale", { date: format.date(worker.lastBeatAt, "datetime") })
              : t("integrity.workerOk", { date: format.date(worker.lastBeatAt, "datetime") })}
        </p>
      </Panel>
      <Panel className="flex flex-col gap-1 p-4 md:p-5">
        <h2 className="font-medium">{t("integrity.backup.title")}</h2>
        <p className={restoreCheck && !restoreCheck.ok ? "text-sm text-danger" : "text-sm text-fg-2"}>
          {!restoreCheck
            ? t("integrity.backup.never")
            : restoreCheck.ok
              ? t("integrity.backup.ok", { date: format.date(restoreCheck.at, "datetime"), count: Number(restoreCheck.detail) })
              : t("integrity.backup.failed", { date: format.date(restoreCheck.at, "datetime"), detail: restoreCheck.detail })}
        </p>
      </Panel>
    </div>
  );
}
